import React, { useCallback } from "react";
import { useDraggable } from '@dnd-kit/core';
import { Box, Typography } from "@mui/material";

const ROW_PADDING = 4;

const GanttBar = ({
    bar,
    rowHeight = 40,
    cellWidth = 8,
    isActive = false,
    isOptimistic = false,
    isOverlay = false,
    onClick,
    onDoubleClick,
    sx = {},
}) => {
    const draggableId = bar.uniqueId || String(bar.id);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: draggableId,
        disabled: isOverlay,
        data: {
            bar,
            source: bar.dragSource,
        },
    });

    const isBeingDragged = !isOverlay && isDragging;

    const opacity = isBeingDragged ? 0.25 : isOptimistic ? 0.8 : 1;

    const left = bar.startSlot * cellWidth;
    const width = bar.duration * cellWidth;
    const top = (bar.laneIndex ?? 0) * rowHeight + ROW_PADDING;

    const isNarrow = width < 60;

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        if (isBeingDragged) return;
        onClick?.(bar, e);
    }, [isBeingDragged, bar, onClick]);

    const handleDoubleClick = useCallback((e) => {
        e.stopPropagation();
        if (isBeingDragged) return;
        onDoubleClick?.(bar, e);
    }, [isBeingDragged, bar, onDoubleClick]);
    
        
    return (
        <Box
            ref={setNodeRef}
            sx={{
                position: "absolute",
                left,
                top,
                width,
                height: rowHeight - ROW_PADDING * 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                bgcolor: bar.color,
                borderRadius: 1,
                boxSizing: "border-box",
                overflow: "visible",
                px: 0.5,
                userSelect: "none",
                touchAction: "none",
                transform: !isBeingDragged && transform
                    ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
                    : undefined,
                transition: isBeingDragged ? "none" : "transform 0.2s ease",
                cursor: isBeingDragged ? "grabbing" : "grab",
                zIndex: isBeingDragged ? 9 : 1,
                opacity,
                outline: isActive ? '1px solid white' : 'none',
                outlineOffset: '-1px',
                '@keyframes barBlink': {
                    '0%, 100%': { filter: 'brightness(1)' },
                    '50%': { filter: 'brightness(1.2)' },
                },
                animation: isActive ? 'barBlink 0.8s ease-in-out infinite' : 'none',
                ...sx,
            }}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            {...listeners}
            {...attributes}
        >
            {bar.memo && (
                <Box sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'warning.main',
                    border: '1px solid',
                    borderColor: 'background.default',
                    zIndex: 2,
                    pointerEvents: 'none',
                }} />
            )}
            {!isNarrow && (
                <Typography 
                    variant="caption" 
                    sx={{
                        fontSize: "0.6rem",
                        textAlign: "center",
                    }}
                >
                    {bar.startTime} {bar.endTime}
                </Typography>
            )}
            <Box 
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
                sx={{ 
                    textAlign: "center", 
                    lineHeight: 1 
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: "0.65rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {bar.shiftCode}
                </Typography>

                <Typography variant="caption" sx={{ fontSize: "0.6rem" }}>
                    {bar.assignedCount}/{bar.requiredEmployees}
                </Typography>
            </Box>
        </Box>
    );
};

export default React.memo(GanttBar);
